import type { KpiDefinitions, NormalizedOrganisation } from 'types/models';

/**
 * Data adapter utilities for normalized /kpi-details response structure
 *
 * The backend now sends KPI trees separately (kpiDefinitions) and organisations
 * reference them by reportingStandardIds instead of duplicating the full tree.
 */

/**
 * Get KPI Areas for an organisation by looking up its reporting standards
 *
 * @param org - Normalised organisation with reportingStandardIds
 * @param kpiDefinitions - Map of reportingStandardId → KPI Areas
 * @returns Array of KPI Areas for this organisation
 */
export function getKpiAreasForOrg(org: any, kpiDefinitions: KpiDefinitions): any[] {
   // Handle legacy structure (already has kpiAreas)
   if (org.kpiAreas) {
      return org.kpiAreas;
   }

   // Handle normalized structure (lookup by reportingStandardIds)
   if (org.reportingStandardIds && Array.isArray(org.reportingStandardIds)) {
      return org.reportingStandardIds.flatMap((rsId: number) => kpiDefinitions[rsId] || []);
   }

   // Handle case where reportingStandards objects exist (extract IDs)
   if (org.reportingStandards && Array.isArray(org.reportingStandards)) {
      return org.reportingStandards.flatMap((rs: any) => kpiDefinitions[rs.id] || []);
   }

   return [];
}

/**
 * Hydrate an organisation with kpiAreas from definitions
 * Creates a new object without mutating the original
 *
 * @param org - Normalized organisation
 * @param kpiDefinitions - Map of reportingStandardId → KPI Areas
 * @returns Organisation with kpiAreas populated
 */
export function hydrateOrganisationWithKpiAreas(org: any, kpiDefinitions: KpiDefinitions): any {
   // If already hydrated (has kpiAreas), return as-is
   if (org.kpiAreas) {
      return org;
   }

   // Get kpiAreas for this org
   const kpiAreas = getKpiAreasForOrg(org, kpiDefinitions);

   // Recursively hydrate subsidiaries
   const hydratedSubsidiaries = org.subsidiaries ? org.subsidiaries.map((sub: any) => hydrateOrganisationWithKpiAreas(sub, kpiDefinitions)) : [];

   return {
      ...org,
      kpiAreas,
      subsidiaries: hydratedSubsidiaries,
   };
}

/**
 * Hydrate all organisations with kpiAreas from definitions
 *
 * @param organisations - Array of normalized organisations
 * @param kpiDefinitions - Map of reportingStandardId → KPI Areas
 * @returns Array of organisations with kpiAreas populated
 */
export function hydrateAllOrganisations(organisations: NormalizedOrganisation[] | any[], kpiDefinitions: KpiDefinitions): any[] {
   if (!organisations || !Array.isArray(organisations)) {
      return [];
   }

   return organisations.map((org) => hydrateOrganisationWithKpiAreas(org, kpiDefinitions));
}

/**
 * Check if response is using normalized structure
 *
 * @param response - API response to check
 * @returns true if normalized structure, false if legacy
 */
export function isNormalizedStructure(response: any): boolean {
   return response && typeof response === 'object' && 'kpiDefinitions' in response && typeof response.kpiDefinitions === 'object';
}

/**
 * Convert normalized response to legacy format for backward compatibility
 *
 * @param response - Normalized response with kpiDefinitions
 * @returns Legacy response with kpiAreas in each organisation
 */
export function normalizedToLegacy(response: any): any {
   if (!isNormalizedStructure(response)) {
      // Already legacy format
      return response;
   }

   const { kpiDefinitions, organisations, ...rest } = response;

   return {
      ...rest,
      organisations: hydrateAllOrganisations(organisations, kpiDefinitions),
   };
}

/**
 * Get all unique reporting standard IDs from organisations
 *
 * @param organisations - Array of normalized organisations
 * @returns Set of unique reporting standard IDs
 */
export function getAllReportingStandardIds(organisations: NormalizedOrganisation[] | any[]): Set<number> {
   const ids = new Set<number>();

   function collectIds(org: any) {
      if (org.reportingStandardIds && Array.isArray(org.reportingStandardIds)) {
         org.reportingStandardIds.forEach((id: number) => ids.add(id));
      }

      if (org.reportingStandards && Array.isArray(org.reportingStandards)) {
         org.reportingStandards.forEach((rs: any) => ids.add(rs.id));
      }

      if (org.subsidiaries && Array.isArray(org.subsidiaries)) {
         org.subsidiaries.forEach(collectIds);
      }
   }

   organisations.forEach(collectIds);
   return ids;
}
